import { Bookmark } from "./bookmark";
import { Layer, SecondaryLayer } from "./layer";

export interface BaseConfig {
  application: ApplicationConfig;
  disclaimerMessage?: string;
  attentionText?: string;
  googleAnalytics: {
    publicMeasurementId: string;
    internalMeasurementId?: string;
  };
  header: HeaderConfig;
  authenticationService?: AuthenticationServiceConfig;
  basemapOptions?: BasemapOptions;
  streetsBasemap: BasemapItem;
  mapOptions: MapOptions;
  proxy?: string;
  printOptions: PrintOptions;
  primaryLayer: Layer;
  secondaryLayers?: SecondaryLayer[];
  bookmarks?: Bookmark[];
  rendererOptions?: RendererOptions;
  resultsOptions?: ResultOptions;
  reportOptions?: ReportOptions;
  searchOptions?: any;
  toolbarOptions: ToolbarOptions;
  libModals?: ModalOptions;
  modals?: ModalOptions;
  popupActions?: PopupAction[];
  environment?: Environment;
  triViewOptions?: TriViewOptions;
  parcelSearchOptions?: ParcelSearchOptions;
  subdivisionSearchOptions?: SubdivisionSearchOptions;
  ownerSearchOptions?: OwnerSearchOptions;
  parcelDetailOptions?: ParcelDetailOptions;
  addressSearchOptions?: AddressSearchOptions;
  addressSearchHEREOptions?: AddressSearchHEREOptions;
  numberOfTypeAheadResults: number;
  parcelLimit: number;
  useProxy?: boolean;
  useSearch?: boolean;
  draggablePlacementOverrides?: {
    [key: string]: "top-right" | "top-left" | "bottom-right" | "bottom-left";
  };
  isPlanningApp?: boolean;
  alternateLayer?: Layer;
}

export interface SummaryExportField {
  label: string;
  field: string;
}

export interface PrintOptions {
  printServiceUrl: string;
  allowDataPrint: boolean;
  excludeDisclaimer: boolean;
  showFormatOptions: boolean;
  showScaleExtentOption: boolean;
  showLegendOption: boolean;
  headerTextColor?: [number, number, number];
  sectionHeaderTextColor?: [number, number, number];
  tableHeaderBackgroundColor?: [number, number, number];
  tableHeaderTextColor?: [number, number, number];
  summaryExportFields?: SummaryExportField[];
}

export interface Environment {
  development: boolean;
}

export interface ParcelSearchOptions {
  url: string;
  field: string;
  suggestUrl: string;
}

export interface SubdivisionSearchOptions {
  url: string;
  field: string;
}

export interface OwnerSearchOptions {
  url: string;
  field: string;
}
export interface ParcelDetailOptions {
  serviceUrl: string;
  documentServiceUrl: string;
  documentLink: string;
}

export interface AddressSearchOptions {
  boundry: Boundry;
}

export interface AddressSearchHEREOptions {
  boundry: Boundry;
  hereSuggestUrlDev: string;
  hereGeocodeUrlDev: string;
  hereSuggestUrl: string;
  hereGeocodeUrl: string;
  appId: string;
  appCode: string;
  notFoundMessage: string;
  streetNameResultMessage: string;
  useApproxResults: boolean;
  minStreetScore: number;
}

export interface Boundry {
  xmin: number;
  ymin: number;
  xmax: number;
  ymax: number;
}

export interface BasemapOptions {
  showBasemapSelector?: boolean;
  default: string;
  basemaps: BasemapItem[];
  groups?: BasemapGroup[];
}

export interface BasemapGroup {
  title: string;
  index?: number;
}

export interface BasemapItem {
  title: string;
  id: string;
  year?: number;
  thumbnail?: string;
  outOfTheBoxBasemap?: string;
  basemapLayers?: Array<BasemapLayerConfig | string>;
  scaleBasemapLayers?: Array<BasemapLayerConfig | string>;
  referenceLayers?: Array<BasemapLayerConfig | string>;
  group?: string;
  index?: number;
  isAerial?: boolean;
  isMobile?: boolean;
}

export interface ModalOptions {
  [key: string]: ModalOption;
}

export interface ModalOption {
  modalId: string;
  visibleOnStartup?: boolean;
}

export interface ToolbarOptions {
  hiddenTools?: { [key: string]: boolean };
  visibleTools?: { [key: string]: boolean };
  initiallyVisibleTools?: { [key: string]: boolean };

  showToolbarTitle?: boolean;
  toolbarBackgroundImage?: string;
  toolbarButtonHighlightColor?: string;
  toolbarButtonTextColor?: string;
}
export interface ReportOptions {
  useReports?: boolean;
  url?: string;
  reports?: ReportOption[];
  uploadImageUrl?: string;
  reportServiceUrl?: string;
  embeddedMapLayer?: Layer;
  additionalOptions?: any;
}

export interface ReportOption {
  name: string;
  report: string;
  mapScreenshot?: boolean;
  mapAspectRatio?: number;
  objectIds?: boolean;
  dataParams: string[];
}

export interface ApplicationConfig {
  nouns: ApplicationNouns;
}

export interface ApplicationNouns {
  plural: string;
  singular: string;
}

export interface HeaderConfig {
  title: string;
  subtitle: string;
  logoUrl?: string;
  showScaleDropdown?: boolean;
  email?: string;
  sourcesUrl?: string;
  rightTitleText?: string;
  rightTitleImage?: string;
  rightTitleBackgroundImage?: string;
}

export interface AuthenticationServiceConfig {
  useAuth: boolean;
  localStorageTokenKey?: string;
  hasAdmin?: boolean;
  hasEditor?: boolean;
  allowedUserRoles?: string[];
  allowedAdminRoles?: string[];
  arcGISAppname: string;
  prodAppUrl: string;
}

export interface MapOptions {
  basemap?: BasemapConfig;
  extent: __esri.ExtentProperties;
  constraints: ConstraintsOptions;
  hoverPopupLayerIds?: string[];
  useHighlightLayer?: boolean;
}

interface BasemapConfig {
  title: string;
  id: string;
  layers: Array<BasemapLayerConfig | string>;
}

export interface BasemapLayerConfig {
  type: string;
  url: string;
  printUrl?: string;
}

interface ConstraintsOptions {
  geometry: __esri.ExtentProperties | __esri.GeometryProperties;
  minZoom?: number;
  maxZoom?: number;
}

export interface RendererOptions {
  titles?: RendererTitleOptions;
  default?: RendererType;
  useRendererSelector?: boolean;
  uniqueValueField?: string;
  points?: __esri.UniqueValueRendererProperties;
  clusters?: ClusterRendererOptions;
  hotSpots?: __esri.HeatmapRendererProperties;
}

export type RendererType = "POINT" | "CLUSTER" | "HOTSPOT";

interface RendererTitleOptions {
  points: string;
  clusters: string;
  hotSpots: string;
}

interface ClusterRendererOptions {
  renderer: __esri.SimpleRendererProperties | any;
  featureReduction: __esri.FeatureReductionClusterProperties;
  singleFeatureLabel: any;
}

export interface ResultControls {
  selection: boolean;
  reports: boolean;
  export: boolean;
}
export interface ResultOptions {
  useGrid?: boolean;
  controls?: ResultControls;
  fields?: ResultFieldOptions[];
  columnLookup?: { [key: string]: any[] };
  isSelectable?: boolean;
  includePagination?: boolean;
  disableToolbar?: boolean;
  title?: string;
}

interface ResultFieldOptions {
  field: string;
  label: string;
  format?: string;
  size: number;
  isDate?: boolean;
  isCurrency?: boolean;
  isVisible: boolean;
  canExport: boolean;
  isButton?: boolean;
  buttonLabel?: string;
}

interface PopupAction {
  actionId: string;
  link: string;
}

export interface SearchDropdownOptions {
  urls: string[];
  field: string;
  blankItemText?: string;
}

export interface TriViewOptions {
  url: string;
  params: TriViewParameter[];
}
interface TriViewParameter {
  key: string;
  value: string;
}

export enum DraggableKeys {
  BUFFER_DRAGGABLE = "BUFFER_DRAGGABLE",
  LAYERS_DRAGGABLE = "LAYERS_DRAGGABLE",
  MARKUP_DRAGGABLE = "MARKUP_DRAGGABLE",
  MEASURE_DRAGGABLE = "MEASURE_DRAGGABLE",
  PARCEL_DETAILS_DRAGGABLE = "PARCEL_DETAILS_DRAGGABLE",
  PRINT_DRAGGABLE = "PRINT_DRAGGABLE",
  RESULTS_DRAGGABLE = "RESULTS_DRAGGABLE",
  SCALE_SETTINGS_DRAGGABLE = "SCALE_SETTINGS_DRAGGABLE",
  BASEMAP_DRAGGABLE = "BASEMAP_DRAGGABLE",
  LEGEND_DRAGGABLE = "LEGEND_DRAGGABLE",
  XY_COORDINATES_DRAGGABLE = "XY_COORDINATES_DRAGGABLE",
}

export enum ModalKeys {
  SCALE_CONFIRMATION = "SCALE_CONFIRMATION",
  MAX_RESULTS_EXCEEDED = "MAX_RESULTS_EXCEEDED",
  NO_RESULTS_FOUND = "NO_RESULTS_FOUND",
  MAX_PRINT_RESULTS_EXCEEDED = "MAX_PRINT_RESULTS_EXCEEDED",
  DISCLAIMER = "DISCLAIMER",
  ZONING_NOTIFICATION = "ZONING_NOTIFICATION",
  SEARCH = "SEARCH",
  EDIT_USER = "EDIT_USER",
  ADD_USER = "ADD_USER",
  DELETE_USER_CONFIRMATION = "DELETE_USER_CONFIRMATION",
  RESET_USER_PASSWORD_CONFIRMATION = "RESET_USER_PASSWORD_CONFIRMATION",
  ADD_BLOCKED_EMAIL = "ADD_BLOCKED_EMAIL",
}
